import classNames from 'classnames';
import React, { memo, useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { COLOR } from '../../styles';
import { a11yKey } from '../../utils/ComponentUtil';
import { type PopupMenuItemProps } from './PopupMenu.types';

const PopupMenuItem: React.FC<PopupMenuItemProps> = memo(
  ({
    dataTestId,
    closeMenu,
    isPopupOpen,
    selected = false,
    refFunc,
    handlerData = {},
    index,
    item: {
      // eslint-disable-next-line
      // @ts-ignore
      attributes,
      // eslint-disable-next-line
      // @ts-ignore
      className,
      // eslint-disable-next-line
      // @ts-ignore
      disabled = false,
      // eslint-disable-next-line
      // @ts-ignore
      groupItems,
      // eslint-disable-next-line
      // @ts-ignore
      icon,
      // eslint-disable-next-line
      // @ts-ignore
      id = null,
      // eslint-disable-next-line
      // @ts-ignore
      isVisible,
      // eslint-disable-next-line
      // @ts-ignore
      onClick,
      // eslint-disable-next-line
      // @ts-ignore
      text,
      // eslint-disable-next-line
      // @ts-ignore
      type,
      // eslint-disable-next-line
      // @ts-ignore
      dataIntercomTarget,
    },
    visibilityHandler = () => true,
  }) => {
    const elementRef = useRef<HTMLElement | null>(null);

    useEffect(() => {
      if (selected && elementRef?.current) {
        elementRef.current.focus();
      }
    }, [selected]);

    const setRef = useCallback(
      (node: HTMLElement) => {
        if (selected && node) {
          node.focus();
        }
        if (!elementRef) {
          // eslint-disable-next-line
          // @ts-ignore
          refFunc(node, `menu-item-${index}`);
          // eslint-disable-next-line
          // @ts-ignore
          elementRef.current = node;
        }
      },
      [index, refFunc, selected],
    );

    const clickHandler = () => {
      if (onClick && !disabled) {
        onClick(handlerData);
      }

      if (isPopupOpen && closeMenu) {
        /* This is to allow any event handlers inside the PopupMenu to finish before unmounting it */
        setTimeout(() => closeMenu());
      }
    };

    const isAction = type === 'action';
    const isLabel = type === 'label';
    const isGroup = type === 'group';
    const isItemVisible =
      // eslint-disable-next-line
      // @ts-ignore
      typeof isVisible === 'boolean' ? isVisible : visibilityHandler({ isVisible });

    const menuItemClassNames = classNames('menu-item', `menu-${type}`, className, {
      disabled,
    });

    return !isItemVisible ? null : (
      <Item
        {...attributes}
        className={menuItemClassNames}
        key={index}
        id={id}
        onClick={clickHandler}
        onKeyDown={a11yKey(clickHandler)}
        ref={setRef}
        tabIndex={isAction ? 0 : -1}
        data-testid={`${dataTestId}-${text}`}
        data-intercom-target={dataIntercomTarget}
      >
        <>
          {isAction && (
            <a className="popup-menu-item_action">
              {!!icon && icon}
              <div>{text}</div>
            </a>
          )}
          {isLabel && (
            <div className="popup-menu-item_label">
              {!!icon && icon}
              {text}
            </div>
          )}
          {isGroup && groupItems}
        </>
      </Item>
    );
  },
);

// Styled Components
const Item = styled.li`
  .popup-menu-item_action,
  .popup-menu-item_label {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    padding: 5px 10px;
    width: 100%;
  }
  .popup-menu-item_action {
    cursor: pointer;
  }

  &.disabled {
    color: ${COLOR['light-gray']};
  }

  &.menu-action:not(.disabled) {
    &:hover,
    &:focus {
      background: ${COLOR['primary']};
      color: ${COLOR['white']};
      outline: none;
    }
  }

  &[tabindex='-1']:focus {
    outline: none;
  }
  .radio-group,
  .radio-group label {
    padding: 5px 10px;
    margin: 0;
  }
`;

// Display Name
PopupMenuItem.displayName = 'PopoverMenuItem';

export default PopupMenuItem;
